import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, catchError, finalize, map, of, switchMap, tap, throwError } from 'rxjs';
// import { API } from '../../api/api';
import { Storage } from '../storage/storage-service';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmDialog } from '../../../shared/components/confirm-dialog/confirm-dialog';
import { PermissionService } from '../permission/PermissionService';
import { RolesService } from '../roles/roles-service';
import {
  Country,
  LoginRequest,
  LoginResponse,
  RegisterRequest,
  RegisterResponse,
  SendOtpRequest,
  VerifyOtpRequest,
} from '../../model/auth.model';
import { API } from '../../api/api';

@Injectable({
  providedIn: 'root',
})
export class Auth {
  private userSubject = new BehaviorSubject<LoginResponse | null>(null);

  user$ = this.userSubject.asObservable();

  constructor(
    private http: HttpClient,
    private router: Router,
    private storage: Storage,
    private dialog: MatDialog,
    private permissionService: PermissionService,
    private rolesService: RolesService,
  ) {
    this.loadUser();
  }

  // =====================================
  // LOAD USER
  // =====================================

  private loadUser(): void {
    const user = this.getCurrentUser();

    if (user) {
      this.userSubject.next(user);
    }
  }

  // =====================================
  // LOGIN
  // =====================================

  login(payload: LoginRequest): Observable<LoginResponse> {
    return this.http
      .post<LoginResponse>(API.AUTH.LOGIN, payload)
      .pipe(switchMap((response) => this.withRolePermissions(response)));
  }

  register(payload: RegisterRequest): Observable<RegisterResponse> {
    return this.http.post<RegisterResponse>(API.AUTH.REGISTER, payload);
  }

  sendOtp(payload: SendOtpRequest): Observable<void> {
    return this.http.post<void>(API.AUTH.SEND_OTP, payload);
  }

  verifyOtp(payload: VerifyOtpRequest): Observable<LoginResponse> {
    return this.http
      .post<LoginResponse>(API.AUTH.VERIFY_OTP, payload)
      .pipe(switchMap((response) => this.withRolePermissions(response)));
  }

  getCountries(): Observable<Country[]> {
    return this.http.get<Country[]>(API.COUNTRIES.LIST);
  }

  refreshAccessToken(): Observable<LoginResponse> {
    const refreshToken = this.storage.getSession('refreshToken');

    if (!refreshToken) {
      return throwError(() => new Error('Refresh token is missing'));
    }

    return this.http.post<LoginResponse>(API.AUTH.REFRESH_TOKEN, { refreshToken }).pipe(
      tap((response) => {
        this.storage.setSession('authToken', response.accessToken);
        this.storage.setSession('refreshToken', response.refreshToken || refreshToken);
      }),
    );
  }

  verifyEmail(userId: number | string, token: string): Observable<any> {
    const params = new HttpParams().set('userId', String(userId)).set('token', token);
    return this.http
      .get(API.AUTH.VERIFY_EMAIL, {
        params,
        responseType: 'text',
      })
      .pipe(
        catchError((error: HttpErrorResponse) => {
          if (error.status === 0 || error.status === 200 || error.status === 204) {
            return of('Email verified successfully.');
          }
          return throwError(() => error);
        }),
      );
  }

  resendVerificationEmail(email: string): Observable<void> {
    return this.http.post<void>(API.AUTH.RESEND_VERIFICATION_EMAIL, { email });
  }

  forgotPassword(email: string): Observable<void> {
    return this.http.post<void>(API.AUTH.FORGOT_PASSWORD, { email });
  }

  resetPassword(payload: {
    email: string;
    token: string;
    password: string;
    passwordConfirmation: string;
  }): Observable<void> {
    return this.http.post<void>(API.AUTH.RESET_PASSWORD, payload);
  }

  changePassword(payload: {
    currentPassword: string;
    newPassword: string;
    confirmPassword: string;
  }): Observable<void> {
    return this.http.post<void>(API.AUTH.CHANGE_PASSWORD, payload);
  }

  // =====================================
  // SAVE AUTH
  // =====================================

  saveAuth(res: LoginResponse): void {
    // =====================================
    // TOKENS
    // =====================================

    this.storage.setSession('authToken', res.accessToken);

    this.storage.setSession('refreshToken', res.refreshToken);

    // =====================================
    // USER DATA
    // =====================================

    this.storage.store('user', res);

    this.storage.store('userId', res.userId);

    this.storage.store('tenantId', res.groupId);

    this.storage.store('email', res.email);

    // The API must return the permissions assigned to this user's role.
    // Store them at login so components can use hasPermission(...) immediately.
    this.permissionService.setPermissions(res.permissions);

    const role = res.role ?? res.roleName;
    if (role !== undefined) {
      this.storage.store('role', role);
    }

    if (res.roleId !== undefined) {
      this.storage.store('roleId', res.roleId);
    }

    this.userSubject.next(res);
  }

  private withRolePermissions(response: LoginResponse): Observable<LoginResponse> {
    // Make the just-issued token available to the auth interceptor for the
    // protected role-permissions request below.
    this.storage.setSession('authToken', response.accessToken);
    this.storage.setSession('refreshToken', response.refreshToken);

    const roleId = response.roleId;
    if (!roleId) {
      return of({ ...response, permissions: [] });
    }

    return this.rolesService.getSelectedRolePermissions(roleId).pipe(
      map((permissions) => ({ ...response, permissions })),
      // Deny UI actions when permissions cannot be loaded; do not retain a previous user's access.
      // The error is logged so an incorrect API URL or a server response is not hidden.
      catchError((error) => {
        console.error(`Unable to load permissions for role ${roleId}.`, error);
        return of({ ...response, permissions: [] });
      }),
    );
  }
  // saveAuth(res: any): void {
  //   this.storage.setSession(
  //     'authToken',
  //     res.access_token,
  //   );

  //   this.storage.store(
  //     'user',
  //     res.user,
  //   );

  //   this.storage.store(
  //     'userId',
  //     res.user.id,
  //   );

  //   this.storage.store(
  //     'role',
  //     res.user.role,
  //   );

  //   this.userSubject.next(res.user);
  // }

  // =====================================
  // ACCESS TOKEN
  // =====================================

  getTenantIdFromToken(): number | null {
    const token = this.getAccessToken();

    if (!token) {
      return null;
    }

    try {
      const payload = JSON.parse(atob(token.split('.')[1]));

      return payload.tenant_id || null;
    } catch (error) {
      console.error('Invalid token', error);
      return null;
    }
  }

  getRoleId(): number | null {
    return this.storage.retrieve('roleId');
  }

  getAccessToken(): string | null {
    return this.storage.getSession('authToken');
  }
  getUserId(): number | null {
    return this.storage.retrieve('userId');
  }

  getTenantId(): number | null {
    return this.storage.retrieve('tenantId');
  }

  getRole(): string {
    return this.storage.retrieve('role') || '';
  }

  getCurrentUser(): LoginResponse | null {
    return this.storage.retrieve('user');
  }

  // =====================================
  // IS LOGGED IN
  // =====================================

  isLoggedIn(): boolean {
    return !!this.getAccessToken();
  }

  // =====================================
  // FORCE LOGOUT
  // =====================================

  forceLogout(): void {
    this.permissionService.clearPermissions();
    this.storage.clear();

    this.userSubject.next(null);

    this.router.navigate(['/login']);
  }

  /** Clear auth state after a refresh failure without prompting the user. */
  handleRefreshFailure(): void {
    this.forceLogout();
  }

  // =====================================
  // MANUAL LOGOUT
  // =====================================

  logout(): void {
    const dialogRef = this.dialog.open(ConfirmDialog, {
      width: '450px',
      data: {
        title: 'Confirm Logout',
        message: 'Are you sure you want to logout?',
        confirmBtnText: 'Logout',
      },
    });

    dialogRef.afterClosed().subscribe((res) => {
      if (res) {
        this.http
          .post<void>(API.AUTH.LOGOUT, {})
          .pipe(finalize(() => this.forceLogout()))
          .subscribe({ error: () => undefined });
      }
    });
  }
}
